import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  FormLabel,
  Input,
  Flex,
  Spinner,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Select,
  Text,
} from '@chakra-ui/react';
import {
  postHistoryReport,
  getProofFile,
  downloadReport,
} from '../api/serviceHistory';
import { format } from 'date-fns';
import ExportReportModal from '../components/exportReportModal';

const ServiceHistoryPage = () => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reportData, setReportData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [parentFilter, setParentFilter] = useState('');
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize] = useState(100);
  const [totalCount, setTotalCount] = useState(0);

  const handleCreateReport = async () => {
    setIsLoading(true);
    const formattedStartDate = new Date(startDate).toISOString();
    const formattedEndDate = new Date(endDate).toISOString();

    try {
      const data = await postHistoryReport({
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        pageNumber,
        pageSize,
      });
      setReportData(data.Items || []);
      setTotalCount(data.TotalCount || 0);
    } catch (error) {
      console.error('Error fetching report:', error);
    } finally {
      setIsLoading(false);
    }
  };
  const handleExport = () => {
    setIsExportModalOpen(true);
  };

  useEffect(() => {
    if (Array.isArray(reportData) && reportData.length > 0) {
      handleCreateReport();
    }
  }, [pageNumber]);

  const handleDownloadReport = fileType => {
    const beginDate = new Date(`${startDate}T00:00`).toISOString();
    const endDateFormatted = new Date(`${endDate}T23:59`).toISOString();

    downloadReport(fileType, {
      startDate: beginDate,
      endDate: endDateFormatted,
    });
  };

  useEffect(() => {
    setPageNumber(1);
  }, [startDate, endDate]);

  const renderPagination = () => {
    const totalPages = Math.ceil(totalCount / pageSize);
    return (
      <Flex mt={4} justifyContent="center" alignItems="center" gap={4}>
        <Button
          onClick={() => setPageNumber(prev => Math.max(prev - 1, 1))}
          isDisabled={pageNumber === 1}
        >
          Previous
        </Button>
        <Box>
          Page {pageNumber} of {totalPages}
        </Box>
        <Button
          onClick={() => setPageNumber(prev => Math.min(prev + 1, totalPages))}
          isDisabled={pageNumber >= totalPages}
        >
          Next
        </Button>
      </Flex>
    );
  };

  const handleClear = () => {
    setStartDate('');
    setEndDate('');
    setParentFilter('');
    setReportData(null);
  };

  const handleDownloadProof = async proof => {
    try {
      const response = await getProofFile(encodeURIComponent(proof));
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', proof);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };

  // Tarih formatlama fonksiyonu
  const formatDate = dateString => {
    try {
      const date = new Date(dateString);
      return format(date, 'dd/MM/yyyy HH:mm:ss'); // İstenilen formatta tarihi döndür
    } catch (error) {
      console.warn(`Invalid date format for: ${dateString}`, error);
      return 'Invalid date';
    }
  };

  const filteredData =
    Array.isArray(reportData) && parentFilter
      ? reportData.filter(item => item.parent === parentFilter)
      : reportData;

  const uniqueParents = Array.isArray(reportData)
    ? [...new Set(reportData.map(item => item.parent))]
    : [];

  return (
    <Flex direction="column" w="full" h="100vh" p={4}>
      <Box bg="gray.100" p={4} rounded="md" mb={4}>
        <Flex justify="space-between" align="center" w="full" gap={4}>
          <Flex direction="column" w="250px" gap={2}>
            <Flex align="center">
              <FormLabel mb="0" mr={2} minW="90px">
                Start Date :
              </FormLabel>
              <Input
                type="date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                size="sm"
              />
            </Flex>
            <Flex align="center">
              <FormLabel mb="0" mr={2} minW="90px">
                End Date :
              </FormLabel>
              <Input
                type="date"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
                size="sm"
              />
            </Flex>
          </Flex>

          <Flex direction="column" w="100px" align="center" gap={2}>
            <Button colorScheme="blue" onClick={handleCreateReport} size="sm">
              Apply
            </Button>
            <Button colorScheme="gray" onClick={handleClear} size="sm">
              Clear
            </Button>
            <Button colorScheme="gray" onClick={handleExport} size="sm">
              Export
            </Button>
          </Flex>
        </Flex>
      </Box>

      <Box flex="1" overflowY="auto" mb={4}>
        {isLoading ? (
          <Spinner size="xl" />
        ) : reportData && reportData.length === 0 ? ( // Eğer data boşsa
          <Box p={4}>
            <Text textAlign="center">No data to display</Text>
          </Box>
        ) : (
          <>
            <Table variant="simple" colorScheme="gray" size="sm">
              <Thead position="sticky" top={0} zIndex={1} bg="white">
                <Tr>
                  <Th>
                    <Select
                      placeholder="Filter"
                      value={parentFilter}
                      onChange={e => setParentFilter(e.target.value)}
                      size="sm"
                      minWidth="150px"
                    >
                      {uniqueParents.map((parent, index) => (
                        <option key={index} value={parent}>
                          {parent}
                        </option>
                      ))}
                    </Select>
                  </Th>
                  <Th>Instrument</Th>
                  <Th>Serial Number</Th>
                  <Th>Service Date</Th>
                  <Th>Company</Th>
                  <Th>Provider</Th>
                  <Th>Authorized</Th>
                  <Th>Proof</Th>
                </Tr>
              </Thead>
              <Tbody>
                {Array.isArray(filteredData) && filteredData.length > 0 ? (
                  filteredData.map((item, index) => (
                    <Tr key={index}>
                      <Td>{item.parent}</Td>
                      <Td>{item.instrument}</Td>
                      <Td>{item.serialNumber}</Td>
                      <Td>{formatDate(item.serviceDate)}</Td>
                      <Td>{item.company}</Td>
                      <Td>{item.provider}</Td>
                      <Td>{item.authorized}</Td>
                      <Td>
                        <Button
                          onClick={() => handleDownloadProof(item.proof)}
                          colorScheme="teal"
                          variant="link"
                          size="sm"
                        >
                          {item.proof}
                        </Button>
                      </Td>
                    </Tr>
                  ))
                ) : (
                  <Tr>
                    <Td colSpan={8}>
                      <Text textAlign="center">No data to display</Text>
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
            {renderPagination()}
          </>
        )}
      </Box>

      <Flex position="fixed" bottom="20px" right="20px" gap={2}>
        <Button colorScheme="gray" size="sm">
          Import
        </Button>
        <Button colorScheme="gray" size="sm">
          Export
        </Button>
      </Flex>
      <ExportReportModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        onDownload={handleDownloadReport}
      />
    </Flex>
  );
};

export default ServiceHistoryPage;
